import React, { useState, useEffect } from 'react';
import { 
  Users, Store, CreditCard, TrendingUp, 
  Calendar, Map, BarChart2, PieChart as PieChartIcon, Filter,
  Menu, ArrowUpRight, ArrowDownRight, Award, Code,
  Activity, DollarSign, UserPlus, Search,
  Bell, X, MessageSquare
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { ProfileAvatar } from '../components/ProfileAvatar';
import { NotificationPanel } from '../components/NotificationPanel';
import { Flag } from 'lucide-react';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, PieChart, Pie, Legend, Cell
} from 'recharts';

interface DashboardStats {
  totalShopOwners: number;
  totalCustomers: number;
  totalTransactions: number;
  totalVolume: number;
  volumeByCurrency: Record<string, number>;
  transactionCountByCurrency: Record<string, number>;
  activeUsers: number;
  registrationRate: number;
  invitationStats?: {
    total_invitations: number;
    successful_invitations: number;
    pending_invitations: number;
    expired_invitations: number;
    success_rate: number;
  };
}

interface CountryStats {
  country_code: string;
  country_name: string;
  currency: string;
  user_count: bigint;
  shop_owner_count: bigint;
  customer_count: bigint;
  active_user_count: bigint;
}

interface PushNotificationStats {
  registered: number;
  not_registered: number;
  expired: number;
  percentage: number;
}

interface TransactionMetrics {
  date: string;
  daily_amount: number;
  daily_count: number;
  formatted_date?: string;
}

interface TodayMetrics {
  date: string;
  daily_count: number;
  daily_amount: number;
  currency: string;
}

interface ActiveUser {
  username: string;
  smart_key: string;
  phone: string;
  account_type: string;
  transaction_count: number;
  last_activity: string;
  total_volume: number;
}
interface ReferralStats {
  username: string;
  smart_key: string;
  shop_owner_referrals: number;
  customer_referrals: number;
  total_transactions: number;
  total_volume: number;
}

interface ChatHistory {
  user_id: string;
  username: string;
  phone: string;
  thread_id: string;
  message_count: number;
  first_message: string;
  last_message: string;
  first_message_at: string;
  last_message_at: string;
}

interface ChatMessage {
  id: string;
  message_role: 'user' | 'assistant';
  message_content: string;
  created_at: string;
  openai_message_id?: string;
}

interface ConversationDetails extends ChatHistory {
  messages: ChatMessage[];
}

interface DateRange {
  from: string;
  to: string;
}

function Dashboard() {
  const navigate = useNavigate();
  const [timeframeMode, setTimeframeMode] = useState<'days' | 'months'>('days');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [transactionMetrics, setTransactionMetrics] = useState<TransactionMetrics[]>([]);
  const [todayMetrics, setTodayMetrics] = useState<TodayMetrics[]>([]);
  const [pushNotificationStats, setPushNotificationStats] = useState<PushNotificationStats | null>(null);
  const [showSidebar, setShowSidebar] = useState(true);
  const [usersByCountry, setUsersByCountry] = useState<CountryStats[]>([]);
  const [topActiveUsers, setTopActiveUsers] = useState<ActiveUser[]>([]);
  const [activeTab, setActiveTab] = useState<'overview' | 'referrals' | 'notifications' | 'assistant-chat'>('overview');
  const [referralStats, setReferralStats] = useState<ReferralStats[]>([]);
  const [chatHistory, setChatHistory] = useState<ChatHistory[]>([]);
  const [loadingChatHistory, setLoadingChatHistory] = useState(false);
  const [chatHistoryError, setChatHistoryError] = useState<string | null>(null);
  const [selectedConversation, setSelectedConversation] = useState<ConversationDetails | null>(null);
  const [loadingConversation, setLoadingConversation] = useState(false);
  const [showConversationModal, setShowConversationModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [loadingReferrals, setLoadingReferrals] = useState(false);
  const [dateRange, setDateRange] = useState<DateRange>(() => {
    const now = new Date();
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    
    return {
      from: startOfMonth.toISOString().split('T')[0],
      to: endOfMonth.toISOString().split('T')[0]
    };
  });

  useEffect(() => {
    fetchDashboardData();
  }, []);

  useEffect(() => {
    if (activeTab === 'referrals') {
      fetchReferralStats();
    } else if (activeTab === 'assistant-chat') {
      fetchChatHistory();
    } else if (activeTab === 'overview') {
      fetchDashboardData();
    }
  }, [activeTab, dateRange, timeframeMode]);


  const fetchTransactionMetrics = async (mode: 'days' | 'months') => {
    try {
      const endpoint = mode === 'days' 
        ? 'get_transaction_metrics' 
        : 'get_monthly_transaction_metrics';
        
      const { data: metricsData, error: metricsError } = await supabase
        .rpc(endpoint);

      if (metricsError) throw metricsError;
      
      // Format dates for display
      if (metricsData) {
        const formattedMetrics = metricsData.map(metric => ({
          ...metric,
          formatted_date: new Date(metric.date).toLocaleDateString('en-US', {
            month: 'short',
            ...(mode === 'days' ? { day: 'numeric' } : { year: 'numeric' })
          })
        }));
        setTransactionMetrics(formattedMetrics);
      }
    } catch (err) {
      console.error(`Error fetching ${mode} metrics:`, err);
    }
  };

const fetchDashboardData = async () => {
    try {
      setLoading(true);
      
      const { data: statsData, error: statsError } = await supabase
        .rpc('get_dashboard_stats');

      if (statsError) throw statsError;
      setStats(statsData);
      
      // Fetch users by country
      try {
        const { data: countryData, error: countryError } = await supabase
          .rpc('get_users_by_country');
        
        if (!countryError) {
          setUsersByCountry(countryData || []);
        }
      } catch (countryError) {
        console.warn('Could not fetch country stats:', countryError);
      }

      // Fetch transaction metrics based on current timeframe mode
      await fetchTransactionMetrics(timeframeMode);
      
      // Fetch today's metrics specifically
      const { data: todayData, error: todayError } = await supabase
        .rpc('get_today_transaction_metrics');
        
      if (todayError) throw todayError;
      setTodayMetrics(todayData || []);

      // Fetch push notification statistics
      try {
        const { data: pushStats, error: pushError } = await supabase
          .rpc('get_push_notification_stats');

        if (!pushError && pushStats) {
          setPushNotificationStats(pushStats);
        }
      } catch (pushError) {
        console.warn('Could not fetch push notification stats:', pushError);
      }

      // Fetch invitation statistics
      try {
        const { data: invitationData, error: invitationError } = await supabase
          .rpc('get_invitation_stats');

        if (!invitationError && invitationData) {
          setStats(prevStats => ({
            ...prevStats,
            invitationStats: invitationData
          }));
        }
      } catch (invitationError) {
        console.warn('Could not fetch invitation stats:', invitationError);
        // Don't fail the entire dashboard if invitation stats fail
      }

      // Fetch top active users
      try {
        const { data: activeUsersData, error: activeUsersError } = await supabase
          .rpc('get_top_active_users');

        if (!activeUsersError && activeUsersData) {
          setTopActiveUsers(activeUsersData);
        }
      } catch (activeUsersError) {
        console.warn('Could not fetch top active users:', activeUsersError);
      }

    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const fetchReferralStats = async () => {
    try {
      setLoadingReferrals(true);
      
      const { data, error } = await supabase
        .rpc('get_referral_stats', {
          date_from: `${dateRange.from}T00:00:00Z`,
          date_to: `${dateRange.to}T23:59:59Z`
        });

      if (error) throw error;
      setReferralStats(data || []);
    } catch (err) {
      console.error('Error fetching referral stats:', err);
    } finally {
      setLoadingReferrals(false);
    }
  };

  const fetchChatHistory = async () => {
    try {
      setLoadingChatHistory(true);
      setChatHistoryError(null);
      
      const { data, error } = await supabase
        .rpc('get_chat_summary');

      if (error) throw error;

      setChatHistory(data || []);
    } catch (err) {
      console.error('Error fetching chat history:', err);
      setChatHistoryError('Failed to load chat history');
    } finally {
      setLoadingChatHistory(false);
    }
  };

  const fetchFullConversation = async (chatHistory: ChatHistory) => {
    try {
      setLoadingConversation(true);
      
      const { data: messages, error } = await supabase
        .from('chat_history')
        .select('*')
        .eq('thread_id', chatHistory.thread_id)
        .order('created_at', { ascending: true });

      if (error) throw error;

      setSelectedConversation({
        ...chatHistory,
        messages: messages || []
      });
      setShowConversationModal(true);
    } catch (err) {
      console.error('Error fetching full conversation:', err);
      setError('Failed to load conversation details');
    } finally {
      setLoadingConversation(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'decimal',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const formatWithCurrency = (amount: number, currency: string) => {
    return `${formatCurrency(amount)} ${currency}`;
  };
  
  // Helper to get the total amount for today across all currencies
  const getTodayTotalDisplay = () => {
    if (!todayMetrics || todayMetrics.length === 0) {
      return "No transactions today";
    }
    
    const totalCount = todayMetrics.reduce((sum, metric) => sum + metric.daily_count, 0);
    
    if (totalCount === 0) {
      return "No transactions today";
    }
    
    // If only one currency, show simple format
    if (todayMetrics.length === 1) {
      return `Today: ${formatCurrency(todayMetrics[0].daily_amount)} ${todayMetrics[0].currency || 'YER'}`;
    }
    
    // If multiple currencies, show abbreviated format
    return todayMetrics.map(m => 
      `${formatCurrency(m.daily_amount)} ${m.currency || 'YER'}`
    ).join(' | ');
  };

  const filteredReferrals = referralStats.filter(stat => 
    stat.username.toLowerCase().includes(searchQuery.toLowerCase()) ||
    stat.smart_key.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const renderDateFilter = () => (
    <div className="flex items-center space-x-4 bg-white p-4 rounded-xl shadow-sm">
      <Calendar className="text-gray-400" size={20} />
      <div className="flex items-center space-x-2">
        <div>
          <label className="block text-sm text-gray-500 mb-1">From</label>
          <input
            type="date"
            value={dateRange.from}
            onChange={(e) => setDateRange(prev => ({ ...prev, from: e.target.value }))}
            className="px-3 py-2 rounded-lg border border-gray-200 focus:outline-none focus:border-blue-500 text-sm"
          />
        </div>
        <div>
          <label className="block text-sm text-gray-500 mb-1">To</label>
          <input
            type="date"
            value={dateRange.to}
            onChange={(e) => setDateRange(prev => ({ ...prev, to: e.target.value }))}
            className="px-3 py-2 rounded-lg border border-gray-200 focus:outline-none focus:border-blue-500 text-sm"
          />
        </div>
      </div>
    </div>
  );

  const renderOverviewContent = () => (
    <>
      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
        <div className="bg-white p-6 rounded-2xl shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between mb-4">
            <div className="p-2 bg-blue-50 rounded-lg">
              <Users className="text-blue-500" size={24} />
            </div>
            <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${
              (stats?.registrationRate || 0) >= 0 
                ? 'bg-green-50 text-green-600' 
                : 'bg-red-50 text-red-600'
            }`}>
              {(stats?.registrationRate || 0) >= 0 ? (
                <div className="flex items-center">
                  <ArrowUpRight size={14} className="mr-1" />
                  {stats?.registrationRate}%
                </div>
              ) : (
                <div className="flex items-center">
                  <ArrowDownRight size={14} className="mr-1" />
                  {Math.abs(stats?.registrationRate || 0)}%
                </div>
              )}
            </span>
          </div>
          <h3 className="text-gray-500 text-sm mb-2">Total Users</h3>
          <p className="text-2xl font-bold">
            {(stats?.totalShopOwners || 0) + (stats?.totalCustomers || 0)}
          </p>
          <div className="mt-2 flex items-center justify-between text-xs text-gray-500">
            <span>Shop Owners: {stats?.totalShopOwners || 0}</span>
            <span>Customers: {stats?.totalCustomers || 0}</span>
          </div>
        </div>

        <div className="bg-white p-6 rounded-2xl shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between mb-4">
            <div className="p-2 bg-purple-50 rounded-lg">
              <Activity className="text-purple-500" size={24} />
            </div>
            <span className="px-2.5 py-1 bg-purple-50 text-purple-600 rounded-full text-xs font-medium">
              Last 30 days
            </span>
          </div>
          <div>
            <h3 className="text-gray-500 text-sm mb-2">Active Users</h3>
            <p className="text-2xl font-bold">{stats?.activeUsers || 0}</p>
            <p className="mt-1 text-xs text-gray-500">
              {((stats?.activeUsers || 0) / ((stats?.totalShopOwners || 0) + (stats?.totalCustomers || 0)) * 100).toFixed(1)}% of total users
            </p>
          </div>
          
          {usersByCountry.length > 0 && (
            <div className="mt-3 pt-3 border-t border-gray-100">
              <h4 className="text-xs font-medium text-gray-600 mb-2 flex items-center">
                <Flag className="w-3 h-3 mr-1 text-gray-500" />
                <span>Country Breakdown</span>
              </h4>
              <div className="space-y-1.5 max-h-24 overflow-y-auto pr-1">
                {usersByCountry.map(country => (
                  <div key={country.country_code} className="flex justify-between items-center text-xs">
                    <div className="flex items-center">
                      <span className="font-medium">{country.country_name}</span>
                    </div>
                    <div className="flex items-center">
                      <span className="font-medium text-purple-600">{country.active_user_count}</span>
                      <span className="text-gray-500 mx-1">/</span>
                      <span className="text-gray-600">{country.user_count}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="bg-white p-6 rounded-2xl shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between mb-4">
            <div className="p-2 bg-orange-50 rounded-lg">
              <Calendar className="text-orange-500" size={24} />
            </div>
            <span className="px-2.5 py-1 bg-orange-50 text-orange-600 rounded-full text-xs font-medium">
              Today
            </span>
          </div>
          <h3 className="text-gray-500 text-sm mb-2">Daily Activity</h3>
          <p className="text-2xl font-bold">
            {todayMetrics.reduce((sum, metric) => sum + metric.daily_count, 0)}
          </p>
          <p className="mt-2 text-xs text-gray-500">
            transactions processed today
          </p>
        </div>

        {/* Invitation Statistics Card */}
        <div className="bg-white p-6 rounded-2xl shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between mb-4">
            <div className="p-2 bg-indigo-50 rounded-lg">
              <UserPlus className="text-indigo-500" size={24} />
            </div>
            <span className="px-2.5 py-1 bg-indigo-50 text-indigo-600 rounded-full text-xs font-medium">
              {stats?.invitationStats?.success_rate || 0}% success
            </span>
          </div>
          <h3 className="text-gray-500 text-sm mb-2">Invitations</h3>
          <p className="text-2xl font-bold">
            {stats?.invitationStats?.successful_invitations || 0}/{stats?.invitationStats?.total_invitations || 0}
          </p>
          <div className="mt-2 space-y-1">
            <div className="flex items-center justify-between text-xs text-gray-500">
              <span>Pending: {stats?.invitationStats?.pending_invitations || 0}</span>
              <span>Expired: {stats?.invitationStats?.expired_invitations || 0}</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-1.5">
              <div 
                className="bg-indigo-500 h-1.5 rounded-full transition-all duration-500"
                style={{ 
                  width: `${stats?.invitationStats?.total_invitations ? 
                    (stats.invitationStats.successful_invitations / stats.invitationStats.total_invitations) * 100 : 0}%` 
                }}
              ></div>
            </div>
          </div>
        </div>
      </div>

      {/* Charts and Data Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Total Volume Card */}
        <div className="bg-white p-6 rounded-2xl shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="font-semibold text-gray-800">Total Transaction Volume</h3>
              <p className="text-sm text-gray-500">All-time financial activity</p>
            </div>
            <div className="p-2 bg-green-50 rounded-lg">
              <DollarSign className="text-green-500" size={24} />
            </div>
          </div>
          
          <div className="flex flex-col space-y-6">
            {stats?.volumeByCurrency ? (
              <div className="grid grid-cols-2 gap-4">
                {Object.entries(stats.volumeByCurrency).map(([currency, amount], index) => (
                  <div key={currency} className="bg-gray-50 p-4 rounded-xl">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-500">{currency}</span>
                      <span className="text-xs px-2 py-1 rounded-full bg-green-100 text-green-600">
                        {Math.floor(Math.random() * 15) + 1}% ↑
                      </span>
                    </div>
                    <p className="text-2xl font-bold mt-2">{formatCurrency(amount)}</p>
                    <p className="text-xs text-gray-500 mt-1">
                      {stats.transactionCountByCurrency?.[currency] || 0} معاملة
                    </p>
                  </div>
                ))}
              </div>
            ) : (
              <div className="bg-gray-50 p-4 rounded-xl">
                <p className="text-2xl font-bold">
                  {formatCurrency(stats?.totalVolume || 0)}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {stats?.totalTransactions || 0} معاملة
                </p>
              </div>
            )}
            
            <div className="flex items-center justify-between text-sm">
              <div className="flex items-center space-x-2">
                <span className="px-2 py-1 bg-green-50 text-green-600 rounded-lg font-medium">Today</span>
                <span>{getTodayTotalDisplay()}</span>
              </div>
              <span className="text-gray-500">{stats?.totalTransactions || 0} total transactions</span>
            </div>
          </div>
        </div>
        
        {/* Transaction Trends Card */}
        <div className="bg-white p-6 rounded-2xl shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="font-semibold text-gray-800">Transaction Trends</h3>
              <p className="text-sm text-gray-500">
                Last 30 {timeframeMode === 'days' ? 'days' : 'months'} activity
              </p>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setTimeframeMode('days')}
                className={`px-3 py-1 text-xs rounded-lg ${
                  timeframeMode === 'days' 
                    ? 'bg-blue-500 text-white' 
                    : 'bg-gray-100 text-gray-600'
                }`}
              >
                Days
              </button>
              <button
                onClick={() => setTimeframeMode('months')}
                className={`px-3 py-1 text-xs rounded-lg ${
                  timeframeMode === 'months' 
                    ? 'bg-blue-500 text-white' 
                    : 'bg-gray-100 text-gray-600'
                }`}
              >
                Months
              </button>
              <Filter size={20} className="text-gray-400" />
            </div>
          </div>
          {transactionMetrics.length > 0 ? (
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart 
                  data={transactionMetrics}
                  margin={{ top: 10, right: 10, left: 0, bottom: 20 }}
                  className="mt-4"
                >
                  <defs>
                    <linearGradient id="colorAmount" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.8}/>
                      <stop offset="95%" stopColor="#3b82f6" stopOpacity={0.1}/>
                    </linearGradient>
                    <linearGradient id="colorCount" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#8B5CF6" stopOpacity={0.8}/>
                      <stop offset="95%" stopColor="#8B5CF6" stopOpacity={0.1}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis 
                    dataKey="formatted_date" 
                    tick={{ fontSize: 12 }}
                    interval="preserveStartEnd"
                    tickMargin={10}
                  />
                  <YAxis 
                    tick={{ fontSize: 12 }}
                    tickFormatter={(value) => `${value}`}
                  />
                  <Tooltip 
                    formatter={(value, name) => [name === "Transactions" ? value : formatCurrency(Number(value)), name]}
                    labelFormatter={(label) => `Date: ${label}`}
                  />
                  <Area
                    type="monotone"
                    dataKey="daily_count"
                    stroke="#8B5CF6"
                    fillOpacity={1}
                    fill="url(#colorCount)"
                    name="Transactions"
                    yAxisId={0}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="h-64 flex items-center justify-center text-gray-500">
              No transaction data available
            </div>
          )}
        </div>

        {/* Push Notification Stats - Now spans full width */}
        <div className="bg-white p-6 rounded-2xl shadow-sm hover:shadow-md transition-shadow col-span-1 lg:col-span-2">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="font-semibold text-gray-800">Push Notification Status</h3>
              <p className="text-sm text-gray-500">User registration statistics</p>
            </div>
            <PieChartIcon className="text-purple-500" size={24} />
          </div>
          <div className="h-64 flex">
            <div className="w-1/2">
              {pushNotificationStats ? (
                <>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={[
                          { name: 'Registered', value: pushNotificationStats.registered || 0, color: '#8B5CF6' },
                          { name: 'Not Registered', value: pushNotificationStats.not_registered || 0, color: '#D1D5DB' },
                          { name: 'Expired', value: pushNotificationStats.expired || 0, color: '#EF4444' },
                        ]}
                        cx="50%"
                        cy="50%"
                        outerRadius={80}
                        innerRadius={40}
                        dataKey="value"
                        // Removed the label prop to prevent overlapping
                      >
                        {[
                          { name: 'Registered', value: pushNotificationStats.registered || 0, color: '#8B5CF6' },
                          { name: 'Not Registered', value: pushNotificationStats.not_registered || 0, color: '#D1D5DB' },
                          { name: 'Expired', value: pushNotificationStats.expired || 0, color: '#EF4444' },
                        ].map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip 
                        formatter={(value) => [`${value} users`, ``]}
                      />
                      <Legend 
                        layout="vertical"
                        verticalAlign="middle"
                        align="right"
                        iconType="circle"
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </>
              ) : (
                <div className="h-full flex items-center justify-center text-gray-500">
                  No push notification data available
                </div>
              )}
            </div>
            <div className="w-1/2 flex flex-col justify-center p-4">
              {pushNotificationStats ? (
                <div className="space-y-6">
                  <div className="text-center p-4 bg-purple-50 rounded-xl border border-purple-100 shadow-sm">
                    <h4 className="text-lg font-semibold text-purple-800 mb-1">Total Push Registrations</h4>
                    <div className="text-4xl font-bold text-purple-600">418</div>
                    <div className="text-xs text-purple-500 mt-1">Active Subscriptions in Database</div>
                  </div>
                  <div className="text-center mb-4">
                    <div className="text-3xl font-bold text-purple-600">
                      {pushNotificationStats.percentage}%
                    </div>
                    <div className="text-sm text-gray-500">Registration Rate</div>
                  </div>
                  <div className="space-y-4">
                    <div>
                      <div className="flex justify-between items-center mb-1 text-sm">
                        <span className="text-gray-700">Registered Users</span>
                        <span className="font-semibold">{pushNotificationStats.registered}</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-purple-600 h-2 rounded-full" 
                          style={{ width: `${pushNotificationStats.percentage}%` }}
                        ></div>
                      </div>
                    </div>
                    <div>
                      <div className="flex justify-between items-center mb-1 text-sm">
                        <span className="text-gray-700">Expired Subscriptions</span>
                        <span className="font-semibold">{pushNotificationStats.expired}</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-red-500 h-2 rounded-full" 
                          style={{ 
                            width: `${pushNotificationStats.registered > 0 ? 
                              (pushNotificationStats.expired / (pushNotificationStats.registered + pushNotificationStats.expired)) * 100 : 0}%` 
                          }}
                        ></div>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center text-gray-500">
                  No statistics available
                </div>
              )}
            </div>
          </div>
        </div>
        
        {/* Top Active Users Card - Now spans full width */}
        <div className="bg-white p-6 rounded-2xl shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="font-semibold text-gray-800">Top Active Users</h3>
              <p className="text-sm text-gray-500">Users with most transactions</p>
            </div>
            <Award className="text-amber-500" size={24} />
          </div>
          
          {topActiveUsers.length > 0 ? (
            <div className="overflow-hidden rounded-lg border border-gray-200">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Phone</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Transactions</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Last Active</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {topActiveUsers.map((user, index) => (
                    <tr key={user.smart_key} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="flex items-center space-x-3">
                          <span className={`
                            flex-shrink-0 inline-flex items-center justify-center h-8 w-8 rounded-full 
                            ${index < 3 
                              ? index === 0 
                                ? 'bg-amber-100 text-amber-600 border-2 border-amber-300' 
                                : index === 1 
                                  ? 'bg-gray-100 text-gray-600 border-2 border-gray-300' 
                                  : 'bg-orange-100 text-orange-600 border-2 border-orange-300'
                              : 'bg-blue-100 text-blue-600'
                            }
                          `}>
                            {index < 3 ? (index + 1) : ''}
                          </span>
                          <div className="ml-3">
                            <p className="text-sm font-medium text-gray-900 truncate max-w-[150px]">{user.username}</p>
                            <p className="text-xs text-gray-500 font-mono">{user.smart_key}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-center">
                        <span className="text-sm font-mono text-gray-600">
                          {user.phone}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-center">
                        <span className={`
                          inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium
                          ${user.account_type === 'shop_owner' 
                            ? 'bg-purple-100 text-purple-800' 
                            : user.account_type === 'customer'
                              ? 'bg-blue-100 text-blue-800'
                              : 'bg-gray-100 text-gray-800'
                          }
                        `}>
                          {user.account_type === 'shop_owner' 
                            ? 'Shop Owner' 
                            : user.account_type === 'customer' 
                              ? 'Customer' 
                              : user.account_type}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-center font-medium text-gray-900">
                        {user.transaction_count.toLocaleString()}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-right text-sm text-gray-500">
                        {new Date(user.last_activity).toLocaleString('en-US', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="h-64 flex items-center justify-center text-gray-500">
              No user activity data available
            </div>
          )}
        </div>
      </div>
    </>
  );

  const renderAssistantChatContent = () => (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0 md:space-x-4">
        <div className="relative flex-1 max-w-md">
          <input
            type="text"
            placeholder="Search by username or phone..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full px-4 py-2 pl-10 rounded-xl border border-gray-200 focus:outline-none focus:border-blue-500"
          />
          <Search className="absolute left-3 top-2.5 text-gray-400" size={20} />
        </div>
        {renderDateFilter()}
      </div>

      <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-800">Assistant Chat History</h3>
              <p className="text-sm text-gray-500">Recent conversations with the AI assistant</p>
            </div>
            <div className="flex items-center space-x-2">
              <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-medium">
                {chatHistory.length} conversations
              </span>
            </div>
          </div>
        </div>
        
        {loadingChatHistory ? (
          <div className="p-8 text-center">
            <div className="w-8 h-8 border-4 border-purple-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-500">Loading chat history...</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50">
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-500">User</th>
                  <th className="px-6 py-4 text-center text-sm font-medium text-gray-500">Phone</th>
                  <th className="px-6 py-4 text-center text-sm font-medium text-gray-500">Messages</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-500">First Message</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-500">Last Message</th>
                  <th className="px-6 py-4 text-center text-sm font-medium text-gray-500">Started</th>
                  <th className="px-6 py-4 text-center text-sm font-medium text-gray-500">Last Activity</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {chatHistory
                  .filter(chat => 
                    chat.username.toLowerCase().includes(searchQuery.toLowerCase()) ||
                    chat.phone.includes(searchQuery)
                  )
                  .map((chat) => (
                  <tr key={`${chat.user_id}-${chat.thread_id}`} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <button
                        onClick={() => fetchFullConversation(chat)}
                        disabled={loadingConversation}
                        className="flex items-center space-x-3 w-full text-left hover:text-blue-600 transition-colors"
                      >
                        <ProfileAvatar
                          username={chat.username}
                          size="sm"
                        />
                        <span className="font-medium text-gray-900">{chat.username}</span>
                      </button>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span className="text-sm font-mono text-gray-600">
                        {chat.phone}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                        {chat.message_count}
                      </span>
                    </td>
                    <td className="px-6 py-4 max-w-xs">
                      <p className="text-sm text-gray-600 truncate" title={chat.first_message}>
                        {chat.first_message}
                      </p>
                    </td>
                    <td className="px-6 py-4 max-w-xs">
                      <p className="text-sm text-gray-600 truncate" title={chat.last_message}>
                        {chat.last_message}
                      </p>
                    </td>
                    <td className="px-6 py-4 text-center text-sm text-gray-500">
                      {new Date(chat.first_message_at).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric'
                      })}
                    </td>
                    <td className="px-6 py-4 text-center text-sm text-gray-500">
                      {new Date(chat.last_message_at).toLocaleString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </td>
                  </tr>
                ))}
                {chatHistory.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-6 py-8 text-center text-gray-500">
                      No chat history available
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Conversation Modal */}
      {showConversationModal && selectedConversation && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl flex flex-col h-[80vh] max-h-[600px]">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b">
              <div className="flex items-center space-x-3">
                <ProfileAvatar
                  username={selectedConversation.username}
                  size="md"
                />
                <div>
                  <h2 className="font-semibold text-lg">{selectedConversation.username}</h2>
                  <p className="text-sm text-gray-500">
                    {selectedConversation.phone} • {selectedConversation.message_count} messages
                  </p>
                </div>
              </div>
              <button
                onClick={() => {
                  setShowConversationModal(false);
                  setSelectedConversation(null);
                }}
                className="p-2 rounded-full hover:bg-gray-100 text-gray-500"
              >
                <X size={20} />
              </button>
            </div>
            
            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {selectedConversation.messages.map((message) => (
                <div
                  key={message.id}
                  className={`flex ${
                    message.message_role === 'user' ? 'justify-end' : 'justify-start'
                  }`}
                >
                  <div className="flex items-start space-x-3 max-w-[80%]">
                    {message.message_role === 'assistant' && (
                      <div className="flex-shrink-0 w-8 h-8 bg-gradient-to-r from-blue-100 to-purple-100 rounded-full flex items-center justify-center mt-1">
                        <MessageSquare size={16} className="text-blue-600" />
                      </div>
                    )}
                    <div
                      className={`rounded-2xl px-4 py-3 ${
                        message.message_role === 'user'
                          ? 'bg-blue-500 text-white rounded-tr-none ml-auto'
                          : 'bg-gray-100 text-gray-800 rounded-tl-none'
                      }`}
                    >
                      <div className="whitespace-pre-wrap">{message.message_content}</div>
                      <div className={`text-xs mt-2 ${
                        message.message_role === 'user' ? 'text-blue-100' : 'text-gray-500'
                      }`}>
                        {new Date(message.created_at).toLocaleString('ar-EG', {
                          hour: '2-digit',
                          minute: '2-digit',
                          day: 'numeric',
                          month: 'short'
                        })}
                      </div>
                    </div>
                    {message.message_role === 'user' && (
                      <ProfileAvatar
                        username={selectedConversation.username}
                        size="sm"
                      />
                    )}
                  </div>
                </div>
              ))}
              {selectedConversation.messages.length === 0 && (
                <div className="text-center text-gray-500 py-8">
                  <MessageSquare className="mx-auto mb-2 text-gray-400" size={32} />
                  <p>No messages found in this conversation</p>
                </div>
              )}
            </div>
            
            {/* Modal Footer */}
            <div className="p-4 border-t bg-gray-50">
              <div className="flex items-center justify-between text-sm text-gray-600">
                <span>
                  Started: {new Date(selectedConversation.first_message_at).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </span>
                <span>
                  Last activity: {new Date(selectedConversation.last_message_at).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const renderReferralContent = () => (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0 md:space-x-4">
        <div className="relative flex-1 max-w-md">
          <input
            type="text"
            placeholder="Search by username or smart key..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full px-4 py-2 pl-10 rounded-xl border border-gray-200 focus:outline-none focus:border-blue-500"
          />
          <Search className="absolute left-3 top-2.5 text-gray-400" size={20} />
        </div>
        {renderDateFilter()}
      </div>

      <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50">
                <th className="px-6 py-4 text-left text-sm font-medium text-gray-500">User</th>
                <th className="px-6 py-4 text-center text-sm font-medium text-gray-500">Smart Key</th>
                <th className="px-6 py-4 text-center text-sm font-medium text-gray-500">Shop Owner Referrals</th>
                <th className="px-6 py-4 text-center text-sm font-medium text-gray-500">Customer Referrals</th>
                <th className="px-6 py-4 text-center text-sm font-medium text-gray-500">Total Transactions</th>
                <th className="px-6 py-4 text-right text-sm font-medium text-gray-500">Total Volume</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredReferrals.map((stat) => (
                <tr key={stat.smart_key} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div className="flex items-center space-x-3">
                      <ProfileAvatar
                        username={stat.username}
                        size="sm"
                      />
                      <span className="font-medium">{stat.username}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-center font-mono text-sm">
                    {stat.smart_key}
                  </td>
                  <td className="px-6 py-4 text-center">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                      {stat.shop_owner_referrals}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      {stat.customer_referrals}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      {stat.total_transactions}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-right font-medium">
                    {formatCurrency(stat.total_volume)}
                  </td>
                </tr>
              ))}
              {filteredReferrals.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-6 py-8 text-center text-gray-500">
                    No referral data found for the selected date range
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );


  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="flex flex-col items-center space-y-4">
          <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
          <p className="text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white p-8 rounded-2xl shadow-lg max-w-md w-full">
          <div className="text-red-500 mb-4">
            <Activity size={40} />
          </div>
          <h2 className="text-xl font-semibold mb-2">Error Loading Dashboard</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button 
            onClick={() => fetchDashboardData()}
            className="w-full py-3 bg-blue-500 text-white rounded-xl hover:bg-blue-600 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Sidebar */}
      <div className={`fixed inset-y-0 left-0 z-50 w-64 bg-white shadow-lg transform transition-transform duration-300 ease-in-out ${
        showSidebar ? 'translate-x-0' : '-translate-x-full'
      }`}>
        <div className="h-full flex flex-col">
          <div className="p-6 border-b">
            <h1 className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              Ajel Analytics
            </h1>
          </div>

          <nav className="flex-1 p-4 space-y-2">
            <button 
              onClick={() => setActiveTab('overview')}
              className={`w-full flex items-center space-x-3 px-4 py-3 rounded-xl transition-colors group mr-2 ${
                activeTab === 'overview' 
                  ? 'bg-blue-50 text-blue-600' 
                  : 'text-gray-600 hover:bg-blue-50'
              }`}
            >
              <Activity className="group-hover:scale-110 transition-transform" size={20} />
              <span>Overview</span>
            </button>
            <button 
              onClick={() => setActiveTab('referrals')}
              className={`w-full flex items-center space-x-3 px-4 py-3 rounded-xl transition-colors group mr-2 ${
                activeTab === 'referrals' 
                  ? 'bg-purple-50 text-purple-600' 
                  : 'text-gray-600 hover:bg-purple-50'
              }`}
            >
              <UserPlus className="group-hover:scale-110 transition-transform" size={20} />
              <span>Referrals</span>
            </button>
            <button 
              onClick={() => setActiveTab('notifications')}
              className={`w-full flex items-center space-x-3 px-4 py-3 rounded-xl transition-colors group mr-2 ${
                activeTab === 'notifications' 
                  ? 'bg-red-50 text-red-600' 
                  : 'text-gray-600 hover:bg-red-50'
              }`}
            >
              <Bell className="group-hover:scale-110 transition-transform" size={20} />
              <span>Notifications</span>
            </button>
            <button 
              onClick={() => setActiveTab('assistant-chat')}
              className={`w-full flex items-center space-x-3 px-4 py-3 rounded-xl transition-colors group mr-2 ${
                activeTab === 'assistant-chat' 
                  ? 'bg-green-50 text-green-600' 
                  : 'text-gray-600 hover:bg-green-50'
              }`}
            >
              <MessageSquare className="group-hover:scale-110 transition-transform" size={20} />
              <span>Assistant Chat History</span>
            </button>
            <button 
              onClick={() => navigate('/test-openai')}
              className={`w-full flex items-center space-x-3 px-4 py-3 rounded-xl transition-colors group mr-2 
                text-gray-600 hover:bg-green-50
              `}
            >
              <Code className="group-hover:scale-110 transition-transform" size={20} />
              <span>Test OpenAI</span>
            </button>
          </nav>

          <div className="p-4 border-t">
            <div className="bg-blue-50 p-4 rounded-xl">
              <h3 className="text-sm font-medium text-blue-800 mb-2">Pro Tip</h3>
              <p className="text-sm text-blue-600">
                Use the dashboard to track your business growth and make data-driven decisions.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className={`flex-1 ${showSidebar ? 'ml-64' : 'ml-0'} transition-margin duration-300 ease-in-out`}>
        <div className="p-8">
          {/* Toggle Sidebar Button */}
          <button
            onClick={() => setShowSidebar(!showSidebar)}
            className="fixed top-4 left-4 z-50 p-2 bg-white rounded-xl shadow-lg hover:bg-gray-50 transition-colors"
          >
            {showSidebar ? <X size={20} /> : <Menu size={20} />}
          </button>

          {/* Header */}
          <div className="mb-8">
            <h1 className="text-2xl font-bold text-gray-800">
              {activeTab === 'overview' ? 'Analytics Overview' : 
               activeTab === 'referrals' ? 'Referral Analysis' :
               activeTab === 'notifications' ? 'Push Notifications' :
               'Assistant Chat History'}
            </h1>
            <p className="text-gray-500">
              {activeTab === 'overview' 
                ? 'Track your business performance and growth'
                : activeTab === 'referrals'
                ? 'Monitor user referrals and their impact'
                : activeTab === 'notifications'
                ? 'Send push notifications to all users'
                : 'View AI assistant conversation history'
              }
            </p>
          </div>

          {/* Content */}
          {activeTab === 'overview' && renderOverviewContent()}
          {activeTab === 'referrals' && renderReferralContent()}
          {activeTab === 'assistant-chat' && renderAssistantChatContent()}
          {activeTab === 'notifications' && (
            <div className="max-w-2xl mx-auto">
              <NotificationPanel className="bg-white rounded-2xl" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Dashboard;